import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch, Prop, Ref } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import EdConsolidateHomeworks from '../ed-consolidate-homeworks/ed-consolidate-homeworks.component.vue';
import { ClassesService } from '@/classes/services/classes.service';

@Component({
  name: 'ed-list-inactive-classes',
  components: {EdConsolidateHomeworks}
})
export default class EdListInactiveClassesComponent extends Vue {

  /* Props */
  @Prop() readonly departmentId: number;
  @Prop() readonly organizationId: number;

  /* Refs */
  @Ref('table') readonly dataTable: any;

  /* Data */
  public departmentResolved: number;
  public loading: boolean = false;
  public selectedClass: any;
  public selectedContent: any;
  public pagedClasses: any[] = [];
  public pagination: object = {
    current: 1,
    total: 0
  }
  public errorMessage : any = '';
  

  /* Computed */
  get  headers() { 
    return [
      { text: this.$t('classes.className'), align: 'start', value: 'name', sortable: false},
      { text: this.$t('classes.headers.grades'), value: 'grade', sortable: false},
      { text: this.$t('classes.headers.schoolCalendar'), value: 'calendar', sortable: false},
      { text: this.$t('classes.headers.teachers'), value: 'teachers',  sortable: false},
      { text: this.$t('classes.headers.courses'), value: 'subjects', sortable: false},
      ]
  }

  public getDates(schoolDay : any) : string{
    return  schoolDay ? `From ${schoolDay.startDate} to ${schoolDay.endDate}` :  ''
  }

  public getSchoolDayDescription(schoolDay : any) : string{
    return  schoolDay ? schoolDay.description :  '-'
  }

  public gradeName(item){
    return item.grade ? item.grade.name : '-';
  }

  public openConsolidatedHomeworks(selectedClass : any, selectedContent: any){
    this.selectedClass = selectedClass;
    this.selectedContent = selectedContent;
    this.$EdModal.createModal(EdConsolidateHomeworks, { 
      classInfo: this.selectedClass,
      content: this.selectedContent
    });
  }


  get getClasses() {
    this.loading = true;
    let page = this.pagination['current']-1;
    let classes = [];

    // Check with store if the page was loaded previously
    classes = this.pagedClasses.find(el => el.page === page)?.classes;

    if (classes) {
        this.loading = false;
        return classes;
    } else if (this.departmentResolved) {
          this.loadClasses(page);
    } else {
      ClassesService.getDepartmentByOrganizationId(this.organizationId)
        .then((res) => {
            this.departmentResolved = res[0].id;
            this.loadClasses(page);
        }).catch((err) => {
          this.loading = false;
          this.error(this.$t('errors.errorLoading'))
        })
    } 
    return classes;
  }

  private loadClasses(page) {
    ClassesService.getInactiveClassesByDepartment(this.departmentResolved, page, 10, 'CALENDAR')
        .then((res) => {
            let classes = res.content;
            this.pagination['total'] = res.totalPages;
            this.pagedClasses = [...this.pagedClasses, { page, classes }];
            this.loading = false;
        }).catch((err) => {
            this.loading = false;
            this.error(this.$t('errors.errorLoading'));
        });
  }

  private error(message : any){
    this.errorMessage = message;
    this.$EdModal.createMessageModal({
      title: this.$i18n.t('common.error'),
      body: this.errorMessage,
      type: KEYS.EVENT_TYPES.DANGER
    });
  }

  public getClassTeachersNames(teachers: any[]): string {
    const template: string = document.querySelector('#item\\.teacher\\.template').innerHTML;
    return teachers?.map(teacher => {
      return template.replace('{teacher.fullName}', teacher.fullName);
    }).join('');
  }

  public getClassSubjectsTitles(clazz: any): string {
    const template: string = document.querySelector('#item\\.subject\\.template').innerHTML;
    const subjects = clazz?.subjects ?? [];
    return subjects?.map((subject: any) => {
      return template
        .replace('{subject.contentTitle}', subject.contentTitle)
        .replace('{subject.classIcon}', `consolidate-homeworks-event-class-${clazz.id}-subject-${subject.contentId}`)
    }).join('');
  }

  public getClassSubjectEvents(): {target: string, handler: Function}[] {
    const events: {target: string, handler: Function}[] = [];
    this.getClasses?.forEach((clazz: any)=> {
      clazz?.subjects?.forEach((subject: any) => {
        events.push({
          target: `.consolidate-homeworks-event-class-${clazz.id}-subject-${subject.contentId}`,
          handler: (params: any) => {
            params.parent.openConsolidatedHomeworks(clazz, subject);
          }
        });
      });
    });
    return events;
  }

  public onPageUpdate(customEvent: CustomEvent): void {
    const detail: { page: number } = customEvent.detail;
    this.pagination['current'] = detail.page;
    this._loadDataTable();
  }

  private _loadEmptyDataTable(): void {
    this.dataTable?.loadData({ items: [], headers: this.headers });
  }

  private _loadDataTable(): void {

    if (!this.dataTable || !this.dataTable.loadData) {
      return;
    }

    if (!this.getClasses || this.getClasses.length == 0) {
      this._loadEmptyDataTable();
      return;
    }

    const slots = {
      'item.name': {
        template: document.querySelector('#item\\.name').innerHTML,
        templateParams: [
          { key: 'item.name',  value: (params: any) => params.item.name},
          { key: 'title.name', value: (params: any) => params.item.name},
        ],
      },
      'item.grade': {
        template: document.querySelector('#item\\.grade').innerHTML,
        templateParams: [
          { key: 'item.grade',  value: (params: any) => params.item.grade?.name ? params.parent.gradeName(params.item) : '-'},
          { key: 'title.grade', value: (params: any) => params.item.grade?.name ? params.parent.gradeName(params.item) : '-'},
        ],
      },
      'item.calendar': {
        template: document.querySelector('#item\\.calendar').innerHTML,
        templateParams: [
          { key: 'item.calendar',  value: (params: any) => params.item.schoolDay ? params.parent.getSchoolDayDescription(params.item.schoolDay) : '-'},
          { key: 'title.calendar', value: (params: any) => params.item.schoolDay ? params.parent.getDates(params.item.schoolDay) : ''},
        ],
      },
      'item.teachers': {
        template: document.querySelector('#item\\.teachers').innerHTML,
        templateParams: [{ 
          key: 'item.teachers',
          value: (params) => params.parent.getClassTeachersNames(params.item.teachers),
        }],
      },
      'item.subjects': {
        template: document.querySelector('#item\\.subjects').innerHTML,
        templateParams: [{
          key: 'item.subjects',
          value: (params) => params.parent.getClassSubjectsTitles(params.item),
        }],
        events: this.getClassSubjectEvents(),
      },
      parent: this,
    };

    this.dataTable.loadData({
      headers: this.headers,
      items: this.getClasses,
      totalItems: this.getClasses.length,
      slots: slots
    });
  }

  @Watch('getClasses', { deep: true, immediate: true })
  private _watchGetClasses(): void {
    this._loadDataTable();
  }

  @Watch('$i18n.locale', { deep: true, immediate: true })
  private _watchLanguage(): void {
    this._loadDataTable();
  }

  beforeMount() {
    this.departmentResolved = this.departmentId;
  }

  mounted() {
    this._loadEmptyDataTable();
  }
}